import { Grid, IconButton, useEventCallback } from '@mui/material';
import { Fragment, memo, ReactElement, useEffect, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { noop } from 'shell/utils/noop';
import {
  CommunicationList,
  ResearchCommunicationFromList,
} from 'civil-status/view-models/communication-from-list';
import PrintIcon from '@mui/icons-material/Print';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import ModalPrintSuccess from 'practice/components/detail/form/print-comunication/modal';
import { toast } from 'react-toastify';
import { ArchivioAnnotazioni } from 'civil-status/view-models/archive-annotations-list';
import { StateCommunication } from 'civil-status/constants/code-state-communications';
import { DateTime } from 'luxon';

interface TableActProps {
  data?: Array<CommunicationList>;
  filterSelected?: ResearchCommunicationFromList;
  getCommunicationListData: (
    data: ResearchCommunicationFromList,
    page?: number,
    rowsPerPage?: number
  ) => void;
  tableProps?: Record<string, number>;
}

const TableCommunicationsComponent = ({
  data,
  filterSelected,
  getCommunicationListData,
  tableProps = {},
}: TableActProps): ReactElement => {
  const [dataList, setDataList] = useState<Array<CommunicationList> | undefined>();
  const [pdfModal, setPdfModal] = useState({
    data: '',
    open: false,
    name: '',
  });
  // ModalPrintSuccess
  const [modalPrintSuccess, setModalPrintSuccess] = useState<{
    open: boolean;
    item?: CommunicationList;
  }>({
    open: false,
  });

  useEffect(() => {
    setDataList(data);
  }, [data]);

  const onPrintCommunication = useHttpClient(`${__ACT_API_URL__}/comunicazioni/stampa`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onClickPrint = useEventCallback((item: CommunicationList) => {
    if (!item.datiComunicazione) {
      return;
    }
    const dataInput = {
      cdCondizione: item.datiComunicazione?.codCondizione || undefined,
      codiceStampa: item.datiComunicazione?.nomeReport || undefined,
      datiComunicazione: item.datiComunicazione,
    };

    (
      onPrintCommunication({
        data: dataInput,
        responseType: 'blob',
      }) as unknown as ResponsePromise<unknown>
    ).then((response) => {
      if (response?.data) {
        const blob = new Blob([(response.data as string) || ''], { type: 'application/pdf' });

        setModalPrintSuccess({
          open: false,
          item,
        });

        blobToBase64(blob).then((base64) => {
          setPdfModal({
            data: base64.split(',')[1] as string,
            name: 'Stampa comunicazione',
            open: true,
          });
        });
      }
    });
  });

  const body: Array<CommunicationList> = useMemo(() => {
    return (dataList || []).map((item) => {
      return {
        ...item,
        action: (
          <div className="pmi-d-flex">
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onClickPrint(item);
              }}
              aria-label="stampa"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
            >
              <PrintIcon />
            </IconButton>
          </div>
        ),
      };
    });
  }, [dataList]);

  const onChangePage = useEventCallback((page: number) => {
    getCommunicationListData(filterSelected || {}, page, tableProps.rowsPerPage);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    getCommunicationListData(filterSelected || {}, 0, rowsPerPage);
  });

  const onUpdateState = useHttpClient(`${__ACT_API_URL__}/dati-comunicazione/update-stato`, {
    options: {
      method: 'PUT',
      loading: true,
      errorCatching: true,
    },
  });

  const onYes = useEventCallback(() => {
    if (!modalPrintSuccess.item?.datiComunicazione) {
      return;
    }

    const dataInput: ArchivioAnnotazioni = {
      ...modalPrintSuccess.item?.datiComunicazione,
      dataRegistrazioneAnnotazione: DateTime.now().toFormat('yyyy-MM-dd'),
      codStatoComunicazione: StateCommunication.StampaEffettuata,
    };
    (
      onUpdateState({
        data: dataInput,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Stato aggiornato');
      setModalPrintSuccess({ open: false });
      getCommunicationListData(filterSelected || {}, tableProps.page, tableProps.rowsPerPage);
    });
  });

  return (
    <Fragment>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Table
            ariaLabel="Lista notifiche"
            page={tableProps.page || 0}
            rowsPerPage={tableProps.rowsPerPage || 10}
            body={body}
            count={(body && body[0]?.totalCount) || 0}
            head={headCells}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            orders={{}}
            isNotLayoutFixed
            noSort
            hideButtons
            isServer
          />
        </Grid>
      </Grid>
      {pdfModal.open ? (
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data}
          name={pdfModal.name}
          title={pdfModal.name || 'Anteprima atto'}
          onClose={() => {
            setModalPrintSuccess((oldState) => ({
              ...oldState,
              open: true,
            }));
            setPdfModal({ data: '', open: false, name: '' });
          }}
        />
      ) : undefined}
      {modalPrintSuccess.open ? (
        <ModalPrintSuccess
          open={modalPrintSuccess.open}
          onYes={onYes}
          onClose={() => onClickPrint(modalPrintSuccess.item || {})}
        />
      ) : undefined}
    </Fragment>
  );
};

TableCommunicationsComponent.displayName = 'Table communication component';

const TableCommunications = memo(TableCommunicationsComponent);

export default TableCommunications;
